// Convert an HTML string to plain text. Strips tags AND decodes entities
// (e.g. &nbsp;, &amp;), which a naive /<[^>]*>/g regex leaves intact and
// causes to leak into UI labels (FECOA-1445).
//
// Uses DOMParser so that "value <5 or >10" survives intact (the regex
// would have eaten "<5 or >" as if it were a tag).
//
// Block-level boundaries (<br>, </p>, </div>, </li>) are converted to
// newlines before parsing so "line1<br>line2" doesn't collapse to
// "line1line2" — the rich text editor stores user newlines as <br>, so
// without this fix multi-line item names render mashed together
// (e.g. "OptionalFECOA-1216", "Multiple ChoiceRequired").
export const htmlToPlainText = (text) => {
  if (!text) return '';
  const html = String(text)
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/(p|div|li|tr|h[1-6])>/gi, '\n');
  if (typeof DOMParser !== 'undefined') {
    return (new DOMParser().parseFromString(html, 'text/html').body.textContent || '').trim();
  }
  // Test/SSR fallback — best-effort, not exhaustive.
  return html
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .trim();
};

export const normalizeQuestionTextForCompare = (text) => htmlToPlainText(text);

export const getStableItemKey = (item) => {
  if (!item) return null;
  return item.item_bank_id || item.content_hash || item.id || null;
};

// Boolean / VAS / numeric / text response types persist their settings as an
// object (e.g. { type: 'boolean', yesLabel, noLabel }), not an array. Only
// ordinal / nominal ship an array of options. The canvas element renderer
// already handles both shapes; this helper just ensures callers don't blindly
// call .map on the object form.
export const buildCanvasOptionsFromUpdatedItem = (responseOptions) => {
  if (!Array.isArray(responseOptions)) return responseOptions;
  return responseOptions.map((opt, i) => ({
    order: i + 1,
    label: opt.label,
    value: opt.value,
  }));
};

const RESPONSE_CONFIG_DEFAULTS = {
  layout: 'vertical',
  spacing: 8,
  columns: 2,
  optionFontSize: 14,
  optionPadding: 8,
  radioSize: 16,
  equalWidth: true,
};

const BOOLEAN_TYPES = ['boolean', 'yes_no', 'yesno'];

// FECOA-1643: a yes/no item persists its answer config as a dict
// ({ type:'boolean', yesLabel, noLabel }) — NOT the options ARRAY that
// ordinal/nominal use. A canvas element that ends up holding that dict (or no
// options at all) renders incorrectly everywhere the builder state is read:
//   • the builder canvas requires Array.isArray(options) and so shows NO
//     answer buttons at all, while
//   • the preview/participant renderer falls back to a hardcoded default
//     "Yes"/"No" radio pair, silently dropping the item's custom labels.
// Given a question element and the backing item's raw options, return the
// normalized 2-option array (honoring any custom labels). Returns null when the
// element isn't yes/no or already has a usable options array, so callers can
// treat null as "leave untouched" — this must never disturb other types or
// well-formed data.
export const deriveBooleanOptionsIfNeeded = (element, rawOptions) => {
  if (!element) return null;
  const type = (
    element.data?.type
    || element.data?.question_type
    || element.data?.response_type
    || element.itemData?.type
    || ''
  ).toLowerCase();
  const isBoolean = BOOLEAN_TYPES.includes(type) || element.responseConfig?.layout === 'boolean';
  if (!isBoolean) return null;

  const existing = element.responseOptions ?? element.data?.options;
  if (Array.isArray(existing) && existing.length > 0) return null;

  const src = rawOptions && typeof rawOptions === 'object' && !Array.isArray(rawOptions)
    ? rawOptions
    : {};
  return [
    { order: 1, label: src.yesLabel || 'Yes', value: 1 },
    { order: 2, label: src.noLabel || 'No', value: 0 },
  ];
};

// Rebuild the canvas element's responseConfig + responseOptions for a given
// response type. Used on initial placement AND after an item's response type
// is edited from the Item Bank — without this, switching e.g. Free Text → Yes/No
// leaves the canvas rendering the old widget (FECOA-1437).
export const buildResponseConfigForType = (responseType, rawOptions) => {
  const isObjectOptions = rawOptions && typeof rawOptions === 'object' && !Array.isArray(rawOptions);

  switch (responseType) {
    case 'vas':
      return {
        responseConfig: {
          ...RESPONSE_CONFIG_DEFAULTS,
          layout: 'vas',
          leftAnchor: (isObjectOptions && rawOptions.leftAnchor) || 'No pain',
          rightAnchor: (isObjectOptions && rawOptions.rightAnchor) || 'Worst pain imaginable',
          minValue: isObjectOptions && rawOptions.minValue != null ? rawOptions.minValue : 0,
          maxValue: isObjectOptions && rawOptions.maxValue != null ? rawOptions.maxValue : 100,
        },
        responseOptions: [],
      };
    case 'numeric':
    case 'nrs':
    case 'rating':
      return {
        responseConfig: {
          ...RESPONSE_CONFIG_DEFAULTS,
          layout: 'nrs',
          minValue: isObjectOptions && rawOptions.minValue != null ? rawOptions.minValue : 0,
          maxValue: isObjectOptions && rawOptions.maxValue != null ? rawOptions.maxValue : 10,
          step: isObjectOptions && rawOptions.step != null ? rawOptions.step : 1,
          leftAnchor: (isObjectOptions && rawOptions.leftAnchor) || '',
          rightAnchor: (isObjectOptions && rawOptions.rightAnchor) || '',
        },
        responseOptions: [],
      };
    case 'text':
      return {
        responseConfig: {
          ...RESPONSE_CONFIG_DEFAULTS,
          layout: 'text',
          maxLength: isObjectOptions && rawOptions.maxLength != null ? rawOptions.maxLength : 500,
          multiline: isObjectOptions && rawOptions.multiline != null ? rawOptions.multiline : true,
        },
        responseOptions: [],
      };
    case 'boolean':
    case 'yes_no':
    case 'yesno':
      return {
        responseConfig: { ...RESPONSE_CONFIG_DEFAULTS, layout: 'boolean' },
        responseOptions: [
          { order: 1, label: (isObjectOptions && rawOptions.yesLabel) || 'Yes', value: 1 },
          { order: 2, label: (isObjectOptions && rawOptions.noLabel) || 'No', value: 0 },
        ],
      };
    case 'ordinal':
    case 'nominal':
    default: {
      const arr = Array.isArray(rawOptions) ? rawOptions : [];
      return {
        responseConfig: { ...RESPONSE_CONFIG_DEFAULTS },
        responseOptions: arr.map((opt, i) => ({
          order: i + 1,
          label: typeof opt === 'string' ? opt : (opt.label || opt.text || `Option ${i + 1}`),
          value: typeof opt === 'object' && opt.value !== undefined ? opt.value : i,
        })),
      };
    }
  }
};

// Which elements on this screen extend past its canvas bounds?
// Tolerance of 1px absorbs sub-pixel rounding from snap/zoom.
export const getScreenOverflowElements = (screen) => {
  if (!screen?.layout || !Array.isArray(screen.elements)) return [];
  const { width: cw, height: ch } = screen.layout;
  if (!cw || !ch) return [];
  return screen.elements.filter((el) => {
    const x = el?.position?.x ?? 0;
    const y = el?.position?.y ?? 0;
    const w = el?.size?.width ?? 0;
    const h = el?.size?.height ?? 0;
    return x < -1 || y < -1 || x + w > cw + 1 || y + h > ch + 1;
  });
};

// Does any element on this screen extend past its canvas bounds?
export const screenHasOverflow = (screen) => getScreenOverflowElements(screen).length > 0;

// Human-readable label for a canvas element (mirrors deleteElement's heuristic).
export const getElementLabel = (element) => {
  const text =
    element?.data?.text ||
    element?.data?.label ||
    element?.label ||
    element?.content ||
    element?.code ||
    '';
  const type = element?.data?.type || element?.type || 'element';
  const typeLabel = String(type).charAt(0).toUpperCase() + String(type).slice(1);
  if (!text) return typeLabel;
  const trimmed = String(text).trim();
  const short = trimmed.length > 40 ? `${trimmed.slice(0, 40)}…` : trimmed;
  return `${typeLabel} · ${short}`;
};
